import PliableLoader from '@components/loaders/PliableLoader.component';
import PageStructure, { PageContent, Pane, PaneContent } from '@pages/PageStructure.component';
import ApiService from '@services/api/api.service';
import { useAuthState } from '@services/auth/auth.service';
import { SurveyAnswers } from '@services/config/config.service';
import { getErrorMessage } from '@services/errors.service';
import TenantConfigService from '@services/tenantConfig.service';
import toast from '@services/toast.service';
import { queryClient, useProjectConfig } from '@stores/data.store';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { EventScheduledEvent, InlineWidget, useCalendlyEventListener } from 'react-calendly';
import { useQuery } from 'react-query';
import { useImmer } from 'use-immer';

interface CalendlyEventInfoData {
    resource: {
        name: string;
        start_time: string;
        end_time: string;
        location: {
            join_url: string;
        }
    }
}

interface CalendlyEventInfoProps {
    eventUuid: string;
}

const useCalendlyEventInfo = (eventUuid: string) => {
    return useQuery(['calendly_onboarding_event', eventUuid], async () => {
        const info = await ApiService.getInstance().request('GET', '/calendly-event') as CalendlyEventInfoData;
        console.log('Event info:', info);
        return info;

    });
}

export const CalendlyEventInfo = ({ eventUuid } : CalendlyEventInfoProps) => {
    const eventInfo = useCalendlyEventInfo(eventUuid);

    if (eventInfo.data) {
        return <>
            <h4>{moment(eventInfo.data?.resource.start_time).format('MMMM Do, YYYY')} at {moment(eventInfo.data?.resource.start_time).format('h:mm a')}</h4>
        </>
    }
    
    return <PliableLoader/>
}

interface Props {
    show: boolean;
    onHide: () => any;
    eventScheduled?: () => any;
}

const BookOnboardingCallModal = (props: Props) => {
    const authState = useAuthState();

    const [answers, setAnswers] = useImmer<SurveyAnswers>({
        onboardingEventLink: '',
        onboardingInviteeLink: '',
    });
    const [tenantConfigId, setTenantConfigId] = useState('');
    const projectConfig = useProjectConfig();

    const [saving, setSaving] = useState(false);
    const saveData = useCallback(async (onboardingEventLink: string, onboardingInviteeLink: string) => {
        setSaving(true);
        try {
            const saveData = {
                ...answers,
                onboardingEventLink,
                onboardingInviteeLink,
            };
            // @ts-ignore
            await TenantConfigService.getInstance().updateOnboardingInfo(tenantConfigId, saveData);
            projectConfig.refetch();
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        } finally {
            setSaving(false);
        }
    }, [answers, tenantConfigId]);

    const onEventScheduled = useCallback((e: EventScheduledEvent) => {
        setAnswers(draft => {
            draft.onboardingEventLink = e.data.payload.event.uri;
            draft.onboardingInviteeLink = e.data.payload.invitee.uri;
            queryClient.invalidateQueries(['calendly_onboarding_event'])
        });
        saveData(e.data.payload.event.uri, e.data.payload.invitee.uri);
        if (props.eventScheduled) {
            props.eventScheduled();
        }
        
    }, [answers]);

    useEffect(() => {
        if (projectConfig.data) {
            setAnswers(projectConfig.data.onboarding_info || {});
            setTenantConfigId(projectConfig.data.id);

        }
    }, [projectConfig.dataUpdatedAt]);

    const alreadyBookedCall = useMemo(() => {
        if (!projectConfig.data) {
            return false;
        }

        return !!projectConfig.data.onboarding_info?.onboardingEventLink;
    }, [projectConfig.dataUpdatedAt]);

    useCalendlyEventListener({
       onEventScheduled: onEventScheduled,
    });

    const innerContent = useMemo(() => {
        if (projectConfig.isLoading) {
            return <PliableLoader/>
        }

        if (alreadyBookedCall) {
            return <div className="card">
                <div className="card-body">
                    <h2>Your call is booked!</h2>
                    <CalendlyEventInfo eventUuid={projectConfig.data?.onboarding_info?.onboardingEventLink as string}/>

                </div>
            </div>
        }

        return <InlineWidget
            styles={{height: '800px', width: '100%'}}
            url="https://calendly.com/d/ckc3-gd4-2wh/pliable-onboarding" 
            prefill={{
                email: authState?.email,
                name: authState?.name,
            }}
            
        />
    }, [projectConfig.dataUpdatedAt, projectConfig.isLoading, alreadyBookedCall, authState]);

    return <Modal show={props.show} onHide={props.onHide} size="xl">
        <Modal.Header closeButton>
            <Modal.Title>Book a call with a product expert</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {innerContent}
        </Modal.Body>
        <Modal.Footer>
            <button className="btn btn-light" onClick={props.onHide}>Close</button>
        </Modal.Footer>
    </Modal>
}

export default BookOnboardingCallModal